import React, { useRef, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Button,
  Stack,
  Typography,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import html2canvas from "html2canvas";
import { ContactsThankYouMessage } from "./ContactsThankYouMessage";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import { formatPhoneForWhatsApp } from "../../../utils/phone";

interface MemoryThankYouCardProps {
  open: boolean;
  onClose: () => void;
  contactName: string;
  contactPhone: string;
  deceasedName: string;
  streamLink: string;
  onSent: () => void;
}

export const ContactsMemoryThankYouCard: React.FC<MemoryThankYouCardProps> = ({
  open,
  onClose,
  contactName,
  contactPhone,
  deceasedName,
  streamLink,
  onSent,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [preparing, setPreparing] = useState(false);

  const initials = (contactName || "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const buildCanvasBlob = async (): Promise<Blob> => {
    const canvas = await html2canvas(cardRef.current!, {
      width: 1080,
      height: 1080,
      scale: 1,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
    return new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject()), "image/png"),
    );
  };

  const handleCopyAndOpen = async () => {
    setPreparing(true);

    try {
      // --- Image step: do this FIRST, while the document still has focus.
      // Clipboard writes require the page to be focused, and window.open()
      // below will steal focus to the new tab, so clipboard must happen
      // before we open anything.
      try {
        const blob = await buildCanvasBlob();

        if (
          navigator.clipboard &&
          "write" in navigator.clipboard &&
          window.ClipboardItem
        ) {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          showSuccessToast("Image copied — paste it into the WhatsApp chat.");
        } else {
          const link = document.createElement("a");
          link.download = `memory-${deceasedName.replace(/\s+/g, "_")}.png`;
          link.href = URL.createObjectURL(blob);
          link.click();
          showSuccessToast(
            "Image downloaded — attach it in the WhatsApp chat.",
          );
        }
      } catch (imgErr) {
        console.error("Thank-you graphic failed:", imgErr);
        showErrorToast(
          "Couldn't copy the image automatically — opening WhatsApp without it.",
        );
        // fall through — still send the message
      }

      // --- WhatsApp step: open the tab now, after the image work is done. ---
      const message = `Hi ${contactName}, I'm Tawanda from Muzukuru Funeral Streaming.\n\nThank you for your Memory message for ${deceasedName}. I've added it to the Memory Book, your words will mean a lot to the family.\n\nYou can also watch the stream here: ${streamLink}`;

      const phone = formatPhoneForWhatsApp(contactPhone);
      if (!phone) {
        showErrorToast("This contact doesn't have a valid phone number.");
        return;
      }

      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      const newTab = window.open(url, "_blank");
      if (newTab) {
        newTab.opener = null;
      }
      const opened = !!newTab;

      if (!opened) {
        showErrorToast(
          "WhatsApp was blocked by your browser's pop-up blocker — allow pop-ups for this site and try again.",
        );
        return;
      }

      onSent();
      onClose();
    } catch (err) {
      console.error("Unexpected error in handleCopyAndOpen:", err);
      showErrorToast("Something went wrong — please try again.");
    } finally {
      setPreparing(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          outline: "none",
          width: 420,
          maxWidth: "92vw",
          overflow: "hidden",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(255,255,255,0.85)",
            "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            zIndex: 1,
          }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                bgcolor: "#724e41",
                width: 40,
                height: 40,
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}
              >
                Send thank-you to {contactName}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                In loving memory of {deceasedName}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Preview, framed like a card rather than a raw image */}
        <Box sx={{ px: 3 }}>
          <Box
            sx={{
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              border: "1px solid #eee",
              width: "100%",
              aspectRatio: "1 / 1",
              position: "relative",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                transform: "scale(0.333)",
                transformOrigin: "top left",
                position: "absolute",
              }}
            >
              <ContactsThankYouMessage deceasedName={deceasedName} />
            </Box>
          </Box>
        </Box>

        {/* Hidden full-size render for the actual capture */}
        <Box sx={{ width: 0, height: 0, overflow: "hidden" }}>
          <Box ref={cardRef} sx={{ width: 1080, height: 1080 }}>
            <ContactsThankYouMessage deceasedName={deceasedName} />
          </Box>
        </Box>

        {/* Footer / action */}
        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
          <Button
            fullWidth
            onClick={handleCopyAndOpen}
            disabled={preparing}
            startIcon={<WhatsAppIcon />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              height: 46,
              bgcolor: "#25D366",
              color: "#fff",
              "&:hover": { bgcolor: "#1ebd5a" },
              "&.Mui-disabled": { bgcolor: "#a8e6c1", color: "#fff" },
            }}
          >
            {preparing ? "Preparing…" : "Copy Image & Open WhatsApp"}
          </Button>
          <Typography
            sx={{
              fontSize: 11.5,
              color: "text.secondary",
              textAlign: "center",
              mt: 1.2,
            }}
          >
            Paste the image (Ctrl/Cmd+V) into the chat that opens, then hit
            send.
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};
